// @core/stores/chat/ChatStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { chatApi } from '@core/api';
import type {
  AddConversationMemberRequest,
  ArchiveConversationRequest,
  Conversation,
  ConversationMemberResponse,
  ConversationResponse,
  CreateConversationRequest,
  CreateMentionTagRequest,
  GetConversationSummaryResponse,
  GetOrCreateDirectConversationRequest,
  GetUnreadCountsResponse,
  LeaveConversationRequest,
  ListConversationMembersResponse,
  ListMentionTagSubscriptionsResponse,
  ListMentionTagsResponse,
  MarkAsReadRequest,
  MentionTagResponse,
  MentionTagSubscriptionResponse,
  SendTypingIndicatorRequest,
  SubscribeToMentionTagRequest,
  UpdateConversationMemberRoleRequest,
  UpdateConversationRequest,
} from '@core/types/chat';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ChatStore {
  // List state
  items: Conversation[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Conversation | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Conversation>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Conversation | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Conversation | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add member to conversation
   */
  async AddConversationMember(conversation_id: string, data: AddConversationMemberRequest): Promise<ConversationMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.AddConversationMember(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Archive conversation
   */
  async ArchiveConversation(conversation_id: string, data: ArchiveConversationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ArchiveConversation(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new conversation
   */
  async CreateConversation(data: CreateConversationRequest): Promise<Conversation> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await chatApi.CreateConversation(data);
      const item = response.conversation;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create a mention tag
   */
  async CreateMentionTag(conversation_id: string, data: CreateMentionTagRequest): Promise<MentionTagResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.CreateMentionTag(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete conversation
   */
  async DeleteConversation(conversation_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await chatApi.DeleteConversation(conversation_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== conversation_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === conversation_id) {
          this.currentItem = null;
        }

        this.removeFromCache(conversation_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete a mention tag
   */
  async DeleteMentionTag(conversation_id: string, mention_tag_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await chatApi.DeleteMentionTag(conversation_id, mention_tag_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== mention_tag_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === mention_tag_id) {
          this.currentItem = null;
        }

        this.removeFromCache(mention_tag_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get conversation details
   */
  async GetConversation(conversation_id: string): Promise<Conversation> {
    // Check cache first
    const cached = this.cache.get(conversation_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await chatApi.GetConversation(conversation_id);
      const item = response.conversation;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get conversation summary
   */
  async GetConversationSummary(conversation_id: string): Promise<GetConversationSummaryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.GetConversationSummary(conversation_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get or create direct conversation with user
   */
  async GetOrCreateDirectConversation(data: GetOrCreateDirectConversationRequest): Promise<ConversationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.GetOrCreateDirectConversation(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get unread counts
   */
  async GetUnreadCounts(params?: { }): Promise<GetUnreadCountsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.GetUnreadCounts(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Leave conversation
   */
  async LeaveConversation(conversation_id: string, data: LeaveConversationRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.LeaveConversation(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List conversation members
   */
  async ListConversationMembers(conversation_id: string, params?: PaginatedRequest): Promise<ListConversationMembersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ListConversationMembers(conversation_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List user's conversations
   */
  async ListConversations(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await chatApi.ListConversations(params);

      runInAction(() => {
        this.items = response.conversations?.conversations ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List mention tags
   */
  async ListMentionTags(conversation_id: string, params?: PaginatedRequest): Promise<ListMentionTagsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ListMentionTags(conversation_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List my mention tag subscriptions
   */
  async ListMyMentionTagSubscriptions(conversation_id: string, params?: PaginatedRequest): Promise<ListMentionTagSubscriptionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.ListMyMentionTagSubscriptions(conversation_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Mark messages as read
   */
  async MarkAsRead(conversation_id: string, data: MarkAsReadRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.MarkAsRead(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove member from conversation
   */
  async RemoveConversationMember(conversation_id: string, profile_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.RemoveConversationMember(conversation_id, profile_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send typing indicator
   */
  async SendTypingIndicator(conversation_id: string, data: SendTypingIndicatorRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.SendTypingIndicator(conversation_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Subscribe to a mention tag
   */
  async SubscribeToMentionTag(conversation_id: string, mention_tag_id: string, data: SubscribeToMentionTagRequest): Promise<MentionTagSubscriptionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.SubscribeToMentionTag(conversation_id, mention_tag_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unsubscribe from a mention tag
   */
  async UnsubscribeFromMentionTag(conversation_id: string, mention_tag_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.UnsubscribeFromMentionTag(conversation_id, mention_tag_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update conversation
   */
  async UpdateConversation(conversation_id: string, data: UpdateConversationRequest): Promise<Conversation> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await chatApi.UpdateConversation(conversation_id, data);
      const item = response.conversation;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update member role
   */
  async UpdateConversationMemberRole(conversation_id: string, profile_id: string, data: UpdateConversationMemberRoleRequest): Promise<ConversationMemberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await chatApi.UpdateConversationMemberRole(conversation_id, profile_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const chatStore = new ChatStore();
